"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.BuyXGetYPromotion = void 0;
const typings_1 = require("../../typings");
const utils_1 = require("../../utils");
const base_1 = require("../base");
class BuyXGetYPromotion extends base_1.BasePromotion {
    constructor(props) {
        super(props);
        this.props = props;
    }
    /**
     * Get free products
     * @returns {ReadonlyArray<FreeProduct>}
     */
    get freeProducts() {
        return this.props.freeProducts;
    }
    /**
     * Get Y products by applying Y products in cart
     * with given multiplier
     *
     * Remark: This will mutate productSKUs in Cart object
     * @param cart
     * @param multiplier
     */
    getFreeProducts(cart, multiplier, matchedCriteriaFreeProducts) {
        const cartWithFreeProducts = Object.assign({}, cart);
        let freeProducts = [];
        let promotionRemarks = [];
        matchedCriteriaFreeProducts.forEach((freeProduct) => {
            const freeProductAmount = freeProduct.amount * multiplier;
            const stockAmountAfterGiveaway = freeProduct.stock - freeProductAmount;
            /**
             * System has enough to giveaway, will giveaway with
             * desire amount and proceed to next iteration
             */
            if (stockAmountAfterGiveaway > 0) {
                freeProducts = freeProducts.concat([
                    Object.assign(Object.assign({}, freeProduct), { amount: freeProductAmount }),
                ]);
                // Remark in promotion on full amount that system can fulfill
                promotionRemarks = promotionRemarks.concat([
                    {
                        fulfilledAmount: freeProductAmount,
                        productSKUId: freeProduct.id,
                        desireAmount: freeProductAmount,
                        missingAmount: 0,
                    },
                ]);
                return;
            }
            /**
             * Stock does not have enough to giveaway,
             * system will give out as much as it has.
             *
             * e.g. stock: 3, giveaway amount: 4
             * system will give out 3 and remark in promotion that
             * the system can only give 3 at maximum
             */
            const fulfilledAmount = stockAmountAfterGiveaway + freeProductAmount;
            promotionRemarks = promotionRemarks.concat([
                {
                    fulfilledAmount,
                    productSKUId: freeProduct.id,
                    desireAmount: freeProductAmount,
                    missingAmount: freeProductAmount - fulfilledAmount,
                },
            ]);
            /**
             * If fulfillable amount is more than 0 meaning that
             * system can giveaway still even if it is less than
             * desire amount, system then push giveaway item to
             * user's cart
             *
             * Remark: This will prevent system from pushing giveaway item
             * into user's cart without actually give anything to user
             */
            if (fulfilledAmount > 0) {
                freeProducts = freeProducts.concat([Object.assign(Object.assign({}, freeProduct), { amount: fulfilledAmount })]);
            }
        });
        return this.composeNewCartWithAppliedPromotion(Object.assign(Object.assign({}, cartWithFreeProducts), { productSKUs: cartWithFreeProducts.productSKUs.concat(freeProducts) }), { remarks: promotionRemarks });
    }
    /**
     * Get matched criterion
     * @param criterion
     * @param matchedCriteria
     * @returns {ReadonlyArray<PromotionCriteria>}
     */
    getMatchedCriterion(criterion, matchedCriteria) {
        if (this.isAutoGeneratedCriteria) {
            return [matchedCriteria];
        }
        return [...criterion, matchedCriteria];
    }
    /**
     * Get multiplier
     * @param currentMultiplier
     * @param matchedCriteria
     * @param productSKUAmount
     * @returns {number}
     */
    getMultiplier(currentMultiplier, matchedCriteria, productSKUAmount) {
        const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, productSKUAmount, this.isAutoGeneratedCriteria);
        if (this.isAutoGeneratedCriteria) {
            return currentMultiplier + multiplier;
        }
        return multiplier;
    }
    /**
     * Get Y product when Buy X piece of selected product
     * e.g. 3 pieces of selected product to get Y
     * @param cart
     */
    applyBuyXGetYForSelectedProductPromotion(cart) {
        const isBundled = this.bundleSetting;
        if (isBundled) {
            throw new Error('Could not apply buy X get Y promotion for X piece of selected product that is mark as bundled');
        }
        if (!cart.productSKUs.length) {
            return cart;
        }
        // Multiplier and Qualified flag to check whether it meets the threshold and how many times should it apply
        let matchedCriterion = [];
        let multiplier = 0;
        let isQualifiedForYProducts = false;
        const newCart = Object.assign({}, cart);
        const productSKUsWithAppliedPromotion = newCart.productSKUs.map((productSKU) => {
            /**
             * If productSKU is free, don't take it into account
             * Skip it completely
             */
            if (productSKU.isFree) {
                return productSKU;
            }
            /**
             * Sku does not belong to promotion
             * calculate total price of current item
             * then proceed to next iteration
             */
            if (!this.selectedProductIds.includes(productSKU.id)) {
                return productSKU;
            }
            /**
             * case: sku amount is less than threshold amount
             * e.g.
             * threshold amount: 3
             * sku amount: 2, price: 30
             */
            const matchedPromotionCriteria = this.getCriteriaByTarget(productSKU.amount);
            if (!matchedPromotionCriteria) {
                return productSKU;
            }
            // Qualified for Y product because it meets the threshold
            matchedCriterion = this.getMatchedCriterion(matchedCriterion, matchedPromotionCriteria);
            multiplier = this.getMultiplier(multiplier, matchedPromotionCriteria, productSKU.amount);
            isQualifiedForYProducts = true;
            return Object.assign(Object.assign({}, productSKU), { promotions: this.addAppliedPromotion(productSKU.promotions) });
        });
        // Return cart if product skus in cart do not reach the threshold or not
        if (!matchedCriterion.length || !isQualifiedForYProducts) {
            return cart;
        }
        const allFreeProducts = matchedCriterion.flatMap((matchedCriteria) => matchedCriteria.freeProducts || []);
        const freeProducts = (0, utils_1.normalizeFreeProducts)(allFreeProducts);
        const hasFreeProducts = (0, utils_1.hasSomeFreeProductsToGiveaway)(freeProducts);
        if (!hasFreeProducts) {
            return cart;
        }
        const cartWithYProducts = this.getFreeProducts(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), multiplier, freeProducts);
        return cartWithYProducts;
    }
    applyBuyXGetYForSelectedProductInBundlePromotion(cart) {
        const isBundled = this.bundleSetting;
        if (!isBundled) {
            throw new Error('Could not apply buy X get Y promotion for X piece of selected product in bundle that is not mark as bundled');
        }
        if (!cart.productSKUs.length) {
            return cart;
        }
        const totalAmountOfSelectedProducts = (0, utils_1.getTotalAmountOfSelectedProducts)(cart.productSKUs, this.selectedProductIds);
        const matchedCriteria = this.getCriteriaByTarget(totalAmountOfSelectedProducts);
        if (!matchedCriteria) {
            return cart;
        }
        const freeProducts = matchedCriteria.freeProducts || [];
        const hasFreeProducts = (0, utils_1.hasSomeFreeProductsToGiveaway)(freeProducts);
        if (!hasFreeProducts) {
            return cart;
        }
        const multiplier = (0, utils_1.getMultiplierByPromotionCriteriaAndDividend)(matchedCriteria, totalAmountOfSelectedProducts, this.isAutoGeneratedCriteria);
        const newCart = Object.assign({}, cart);
        const productSKUsWithAppliedPromotion = this.addAppliedPromotionToProductSKUs(newCart.productSKUs);
        const cartWithYProducts = this.getFreeProducts(Object.assign(Object.assign({}, newCart), { productSKUs: productSKUsWithAppliedPromotion }), multiplier, freeProducts);
        return cartWithYProducts;
    }
    applyPromotion(cart) {
        switch (this.settingCondition) {
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT:
                return this.applyBuyXGetYForSelectedProductPromotion(cart);
            case typings_1.PromotionCondition.BUY_X_PIECE_OF_SELECTED_PRODUCT_IN_BUNDLE:
                return this.applyBuyXGetYForSelectedProductInBundlePromotion(cart);
            default:
                return cart;
        }
    }
}
exports.BuyXGetYPromotion = BuyXGetYPromotion;
//# sourceMappingURL=buy-x-get-y.js.map